import React, { useState, useRef } from 'react';
import { Camera, X, Upload, User } from 'lucide-react';
import '../../styles/components/profile-image-upload.scss';

interface ProfileImageUploadProps {
  currentImage?: string;
  onImageSelected: (file: File) => void;
  onImageRemoved?: () => void;
  maxSize?: number; // in MB
  disabled?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

const ProfileImageUpload: React.FC<ProfileImageUploadProps> = ({
  currentImage,
  onImageSelected,
  onImageRemoved,
  maxSize = 5, // 5MB default
  disabled = false,
  size = 'md'
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImage || null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateImage = (file: File): string | null => {
    // Check file type
    if (!file.type.startsWith('image/')) {
      return 'Solo se permiten archivos de imagen';
    }

    // Check file size
    if (file.size > maxSize * 1024 * 1024) {
      return `La imagen no debe superar ${maxSize}MB`;
    }

    return null;
  };

  const handleFileSelect = (file: File) => {
    const validationError = validateImage(file);

    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Call callback
    onImageSelected(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewUrl(null);
    setError(null);
    if (onImageRemoved) {
      onImageRemoved();
    }
  };

  const handleBrowseClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div className={`profile-image-upload profile-image-upload--${size} ${disabled ? 'disabled' : ''}`}>
      <div
        className={`profile-image-upload__container ${isDragging ? 'dragging' : ''}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />

        {previewUrl ? (
          <div className="profile-image-upload__preview">
            <img src={previewUrl} alt="Profile" />
            <div className="profile-image-upload__overlay">
              <button
                type="button"
                className="profile-image-upload__change-btn"
                onClick={handleBrowseClick}
              >
                <Camera size={20} />
                <span>Cambiar</span>
              </button>
              {onImageRemoved && (
                <button
                  type="button"
                  className="profile-image-upload__remove-btn"
                  onClick={handleRemoveImage}
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        ) : (
          <div
            className="profile-image-upload__placeholder"
            onClick={handleBrowseClick}
          >
            <div className="profile-image-upload__icon">
              <User size={size === 'lg' ? 48 : size === 'md' ? 36 : 24} />
            </div>
            <div className="profile-image-upload__upload-icon">
              <Upload size={size === 'lg' ? 24 : 18} />
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="profile-image-upload__error">
          {error}
        </div>
      )}

      <div className="profile-image-upload__help">
        Click o arrastra una imagen · JPG, PNG, GIF · Máx {maxSize}MB
      </div>
    </div>
  );
};

export default ProfileImageUpload;
